export default function FeatureCockpitSupport() {
  return (
    <section id="cockpit-support" className="py-16 sm:py-24 bg-gradient-to-b from-[#0A1628] to-[#0D1B2E]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-12 sm:mb-16">
          <div className="w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-2xl flex-shrink-0">
            <i className="ri-brain-line text-2xl sm:text-3xl text-[#00FFD1]"></i>
          </div>
          <div>
            <div className="text-xs text-[#00FFD1] uppercase tracking-widest font-semibold mb-2">Capability 06</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">Cockpit Workload Support</h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-300 max-w-3xl leading-relaxed">
              In high-workload moments, WingmanFix acts as a calm second brain — surfacing the right information at the right time, without adding to your cognitive load.
            </p>
          </div>
        </div>

        {/* Scenario Panel - stacks vertically on mobile */}
        <div className="grid lg:grid-cols-2 gap-1 rounded-3xl overflow-hidden border border-[#00FFD1]/30 mb-8 sm:mb-12 shadow-2xl">
          {/* Left: Cockpit Alert */}
          <div className="bg-[#0D1B2E] p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse"></div>
              <span className="text-xs sm:text-sm font-semibold text-gray-300 uppercase tracking-widest">Cockpit Display</span>
            </div>

            <div className="bg-[#1A0A0A] border-2 border-red-500/70 rounded-2xl p-4 sm:p-6 mb-4 sm:mb-6">
              <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-5">
                <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center">
                  <i className="ri-alert-line text-3xl sm:text-4xl text-red-500"></i>
                </div>
                <div>
                  <p className="text-lg sm:text-xl font-bold text-red-400">Oil Pressure Alert</p>
                  <p className="text-xs sm:text-sm text-gray-400">Immediate attention required</p>
                </div>
              </div>
              <div className="bg-[#0A1628] rounded-xl p-3 sm:p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-xs text-gray-500 uppercase tracking-wider">Oil Pressure</span>
                  <span className="text-xs text-red-400 font-bold">18 PSI — LOW</span>
                </div>
                <div className="h-4 bg-[#1A2942] rounded-full overflow-hidden">
                  <div className="h-full w-1/5 bg-red-500 rounded-full"></div>
                </div>
                <div className="flex justify-between mt-1.5">
                  <span className="text-xs text-gray-600">0</span>
                  <span className="text-xs text-gray-600">Normal: 55–95 PSI</span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2 sm:gap-3">
              {[
                { label: 'Altitude', value: '4,200 ft', icon: 'ri-arrow-up-line' },
                { label: 'Airspeed', value: '112 KIAS', icon: 'ri-speed-line' },
                { label: 'Heading', value: '285°', icon: 'ri-compass-3-line' },
                { label: 'Fuel', value: '28 gal', icon: 'ri-gas-station-line' },
              ].map((item, i) => (
                <div key={i} className="bg-[#0A1628] rounded-xl p-3 sm:p-4 border border-gray-700 flex items-center gap-2 sm:gap-3">
                  <div className="w-6 h-6 sm:w-8 sm:h-8 flex items-center justify-center">
                    <i className={`${item.icon} text-lg sm:text-xl text-gray-400`}></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">{item.label}</p>
                    <p className="text-xs sm:text-sm font-bold text-white">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: WingmanFix Panel */}
          <div className="bg-[#050A14] p-6 sm:p-8 lg:p-10">
            <div className="flex items-center gap-3 mb-6 sm:mb-8">
              <div className="w-3 h-3 rounded-full bg-[#00FFD1] animate-pulse"></div>
              <span className="text-xs sm:text-sm font-semibold text-[#00FFD1] uppercase tracking-widest">WingmanFix Suggestion</span>
            </div>

            <div className="space-y-3 sm:space-y-4">
              {[
                {
                  icon: 'ri-error-warning-line',
                  title: 'Possible oil pressure failure detected',
                  sub: 'Recommend precautionary landing immediately',
                  color: '#00FFD1',
                },
                {
                  icon: 'ri-map-pin-line',
                  title: 'Nearest airport: 3.8 NM',
                  sub: 'KXYZ — Bearing 285° — Runway 28, 4,200 ft',
                  color: '#00FFD1',
                },
                {
                  icon: 'ri-speed-line',
                  title: 'Best glide: 65 knots',
                  sub: 'Maintain for maximum glide range',
                  color: '#00A3E0',
                },
                {
                  icon: 'ri-file-list-3-line',
                  title: 'Emergency checklist loaded',
                  sub: 'Engine failure — 7 steps — tap to expand',
                  color: '#B4FF39',
                },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3 sm:gap-4 bg-[#0D1B2E] rounded-xl p-4 sm:p-5 border border-[#00FFD1]/20 hover:border-[#00FFD1]/40 transition-all cursor-pointer">
                  <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center flex-shrink-0">
                    <i className={`${item.icon} text-xl sm:text-2xl`} style={{ color: item.color }}></i>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-semibold text-white mb-0.5">{item.title}</p>
                    <p className="text-xs text-gray-400">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 sm:mt-8 bg-[#0D1B2E] rounded-2xl p-4 sm:p-5 border border-gray-700">
              <div className="flex items-start gap-3">
                <i className="ri-information-line text-[#00A3E0] text-lg sm:text-xl mt-0.5 flex-shrink-0"></i>
                <p className="text-xs sm:text-sm text-gray-400 leading-relaxed italic">
                  WingmanFix never flies the airplane. It simply helps you think faster when seconds matter.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Feature grid - collapses to 1-col on mobile */}
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6">
          {[
            {
              icon: 'ri-focus-3-line',
              title: 'Zero Distraction Design',
              desc: 'All suggestions are passive and non-intrusive. WingmanFix never interrupts your scan or demands attention.',
              color: '#00FFD1',
            },
            {
              icon: 'ri-shield-check-line',
              title: 'Checklist Integration',
              desc: 'Emergency and normal checklists are pre-loaded and surface automatically based on detected flight phase.',
              color: '#00A3E0',
            },
            {
              icon: 'ri-map-2-line',
              title: 'Nearest Airport Lookup',
              desc: 'Instantly surfaces the closest suitable airport with runway length, bearing, and distance — no searching required.',
              color: '#B4FF39',
            },
          ].map((item, i) => (
            <div key={i} className="bg-[#0F1F35] rounded-2xl p-6 sm:p-8 border border-gray-700 hover:border-[#00FFD1]/30 transition-all">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center mb-4 sm:mb-5 rounded-xl" style={{ backgroundColor: `${item.color}15` }}>
                <i className={`${item.icon} text-xl sm:text-2xl`} style={{ color: item.color }}></i>
              </div>
              <h3 className="text-base sm:text-lg font-bold text-white mb-2 sm:mb-3">{item.title}</h3>
              <p className="text-xs sm:text-sm text-gray-400 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}